import type * as React from 'react';

export interface PaginationProps {
  limit: number;
  offset: number;
  total: number;
  onPageChange: (newOffset: number) => void;
}
/**
 * Component for displaying required form field error
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-06-16
 * @lastUpdated
 * @version 1.1.0
 */
export const Pagination = ({
  limit,
  offset,
  total,
  onPageChange,
}: PaginationProps): React.JSX.Element => {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const pageRange = 2; //Number of pages to show around the current page

  if (totalPages <= 1) return null; // Hide pagination if only one page

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - pageRange);
    const endPage = Math.min(totalPages, currentPage + pageRange);

    if (startPage > 1) pages.push(1); // Always show the first page
    if (startPage > 2) pages.push('...'); // Ellipsis before the first set of pages

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) pages.push('...'); // Ellipsis after the last set of pages
    if (endPage < totalPages) pages.push(totalPages); // Always show the last page

    return pages;
  };

  return (
    <div className="pagination mt-2">
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          {/* First Page */}
          <li className={`page-item ${offset === 0 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(0)}>
              «
            </button>
          </li>

          {/* Previous Page */}
          <li className={`page-item ${offset === 0 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(offset - limit)}>
              ‹
            </button>
          </li>

          {/* Page Numbers with Ellipsis */}
          {getPageNumbers().map((page, index) => (
            <li
              key={index}
              className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
            >
              {page === '...' ? (
                <span className="page-link">...</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => onPageChange((Number(page) - 1) * limit)}
                >
                  {page}
                </button>
              )}
            </li>
          ))}

          {/* Next Page */}
          <li className={`page-item ${offset + limit >= total ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(offset + limit)}>
              ›
            </button>
          </li>

          {/* Last Page */}
          <li className={`page-item ${offset + limit >= total ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange((totalPages - 1) * limit)}>
              »
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};
